// Attachments (device page, column 3): install photos, wiring photos, job sheets about the vehicle
// or the tracker. Photos show as thumbnails; files open in a new tab only for logged-in staff.
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Attachment, extras } from '../api';
import { useAuth } from '../auth';
import { ago, dateTime } from '../format';

const MAX_MB = 20;
const size = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const showable = (t: string) => t === 'image/jpeg' || t === 'image/png' || t === 'image/webp';

export function Attachments({ subject, id, title }: { subject: 'vehicles' | 'trackers'; id: number; title: string }) {
  const { user } = useAuth();
  const [list, setList] = useState<Attachment[] | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    extras.attachments(subject, id).then((r) => { setList(r.attachments); setError(''); }).catch((e) => setError((e as Error).message));
  }, [subject, id]);
  useEffect(load, [load]);

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    setError('');
    for (const [i, file] of files.entries()) {
      if (file.size > MAX_MB * 1024 * 1024) { setError(`${file.name} is larger than ${MAX_MB} MB.`); continue; }
      setBusy(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : 'Uploading…');
      try {
        await extras.attach(subject, id, file, note.trim());
      } catch (err) {
        setError(`${file.name}: ${(err as Error).message}`);
      }
    }
    setBusy('');
    setNote('');
    load();
  };
  const del = async (a: Attachment) => {
    if (!window.confirm(`Delete ${a.fileName}?`)) return;
    try {
      await extras.deleteAttachment(a.id);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const open = (a: Attachment) => window.open(extras.fileUrl(a.id), '_blank', 'noopener');

  return (
    <section className="card form" aria-label={title}>
      <div className="page-head"><h2>{title}</h2></div>
      <div className="attach-add">
        <label className="sr-only" htmlFor={`note-${subject}-${id}`}>Note for the next upload</label>
        <input id={`note-${subject}-${id}`} type="text" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. wiring behind dash" />
        <button type="button" className="button ghost small" disabled={!!busy} onClick={() => input.current?.click()}>{busy || 'Add photos or PDF…'}</button>
        <input ref={input} type="file" multiple hidden accept="image/jpeg,image/png,image/webp,image/heic,.heic,application/pdf" onChange={(e) => void upload(e)} />
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {list === null && !error && <p className="muted">Loading…</p>}
      {list?.length === 0 && <p className="muted small">No files yet. Photos and PDFs up to {MAX_MB} MB.</p>}
      {list && list.length > 0 && (
        <ul className="attach-list">
          {list.map((a) => (
            <li key={a.id}>
              <button type="button" className="attach-thumb" onClick={() => open(a)} aria-label={`Open ${a.fileName}`}>
                {showable(a.contentType)
                  ? <img src={extras.fileUrl(a.id)} alt="" loading="lazy" />
                  : <span className="attach-type">{a.contentType === 'application/pdf' ? 'PDF' : 'HEIC'}</span>}
              </button>
              <div className="attach-meta">
                <button type="button" className="link attach-name" onClick={() => open(a)}>{a.fileName}</button>
                {a.note && <span className="small">{a.note}</span>}
                <span className="muted small">
                  {size(a.sizeBytes)} · {a.uploadedBy ?? 'someone'} · <time dateTime={a.uploadedAt ?? undefined} title={a.uploadedAt ? dateTime(a.uploadedAt) : undefined}>{ago(a.uploadedAt)}</time>
                  {(user?.role === 'admin' || a.uploadedById === user?.id) && <> · <button type="button" className="link danger small" onClick={() => void del(a)}>Delete</button></>}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
